import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Layout from "../../../../layout/default";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaBuilding,
  FaCity,
  FaMapPin,
  FaGlobeAsia,
  FaExchangeAlt,
  FaCheckCircle,
  FaAddressCard,
} from "react-icons/fa";

const AddUserAddressDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    state_id: "",
    district_id: "",
    city_id: "",
    zone_id: "",
    address: "",
    billing_address: "",
    billing_same_as_shipping: false,
    shipping_phone_number: "",
    billing_phone_number: "",
    shipping_pincode: "",
    billing_pincode: "",
    billing_state_id: "",
    billing_district_id: "",
    billing_city_id: "",
    billing_zone_id: "",
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const authToken = localStorage.getItem("authToken");

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.address || !formData.billing_address) {
      return Swal.fire({
        icon: "error",
        title: "Invalid Input",
        text: "Please provide both Shipping and Billing addresses.",
      });
    }

    const payload = {
      shipping_address: {
        address: formData.address,
        phone_number: formData.shipping_phone_number,
        pincode: formData.shipping_pincode,
        state_id: formData.state_id,
        district_id: formData.district_id,
        city_id: formData.city_id,
        ...(formData.zone_id && { zone_id: formData.zone_id }), // Optional zone
      },
      billing_address: {
        address: formData.billing_address,
        phone_number: formData.billing_phone_number,
        pincode: formData.billing_pincode,
        state_id: formData.billing_state_id,
        district_id: formData.billing_district_id,
        city_id: formData.billing_city_id,
        ...(formData.billing_zone_id && { zone_id: formData.billing_zone_id }), // Optional zone
      },
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_HOST}api/address`,
        payload,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      Swal.fire("Success!", "Address added successfully.", "success");
      navigate("/shop");
    } catch (error) {
      let msg = "Address failed. Please try again.";
      if (error.response?.data?.errors) {
        msg = Object.values(error.response.data.errors)
          .flat()
          .join(" ");
      }
      Swal.fire("Error!", msg, "error");
    }
  };

  // Load states on mount
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_HOST}api/states`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => setStates(res.data))
      .catch(() => Swal.fire("Error!", "Failed to load states.", "error"));
  }, []);

  // Load districts when state changes
  useEffect(() => {
    if (!formData.state_id) {
      setDistricts([]);
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_HOST}api/state_districts?state_id=${formData.state_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => setDistricts(res.data.data || []))
      .catch(() => Swal.fire("Error!", "Failed to fetch districts.", "error"));
  }, [formData.state_id]);

  // Load cities when district changes
  useEffect(() => {
    if (!formData.district_id) {
      setCities([]);
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_HOST}api/district_cities?district_id=${formData.district_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => setCities(res.data.data || []))
      .catch(() => Swal.fire("Error!", "Failed to fetch cities.", "error"));
  }, [formData.district_id]);

  // Load zones when city changes - SILENTLY HANDLE NO ZONES CASE
  useEffect(() => {
    if (!formData.city_id) {
      setZones([]);
      return;
    }
    
    setLoadingZones(true);
    axios
      .get(
        `${process.env.REACT_APP_HOST}api/city_zones?city_id=${formData.city_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setZones(res.data.data || []);
      })
      .catch((error) => {
        // Silently handle 404 (no zones) - don't show error
        if (error.response?.status !== 404) {
          Swal.fire("Error!", "Failed to fetch zones.", "error");
        }
        setZones([]);
      })
      .finally(() => {
        setLoadingZones(false);
      });
  }, [formData.city_id]);

  // Generic change handler
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Sync billing fields when checkbox enabled
    if (name === "billing_same_as_shipping" && checked) {
      setFormData((prev) => ({
        ...prev,
        billing_address: prev.address,
        billing_phone_number: prev.shipping_phone_number,
        billing_pincode: prev.shipping_pincode,
        billing_state_id: prev.state_id,
        billing_district_id: prev.district_id,
        billing_city_id: prev.city_id,
        billing_zone_id: prev.zone_id,
      }));
    }
  };

  return (
    <Layout title="Add Address">
      <Container className="mt-4">
        <Row className="justify-content-md-center">
          <Col md={8} className="shadow p-4 rounded bg-light">
            <h4 className="text-center text-primary mb-4">
              <FaAddressCard className="me-2" /> Add Address Details
            </h4>
            <Form onSubmit={handleSubmit}>
              {/* Shipping Section */}
              <h5 className="text-success mb-3">
                <FaMapMarkerAlt className="me-2" /> Shipping Address
              </h5>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaBuilding className="me-2" /> Address
                </Form.Label>
                <Form.Control
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaPhoneAlt className="me-2" /> Phone Number
                </Form.Label>
                <Form.Control
                  name="shipping_phone_number"
                  value={formData.shipping_phone_number}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaMapPin className="me-2" /> Pincode
                </Form.Label>
                <Form.Control
                  name="shipping_pincode"
                  value={formData.shipping_pincode}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>
                      <FaGlobeAsia className="me-2" /> State
                    </Form.Label>
                    <Form.Select
                      name="state_id"
                      value={formData.state_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>
                      <FaMapMarkerAlt className="me-2" /> District
                    </Form.Label>
                    <Form.Select
                      name="district_id"
                      value={formData.district_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select District</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>
                      <FaCity className="me-2" /> City
                    </Form.Label>
                    <Form.Select
                      name="city_id"
                      value={formData.city_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Only show zone field if zones exist for this city */}
                {zones.length > 0 && (
                  <Col>
                    <Form.Group>
                      <Form.Label>
                        <FaMapPin className="me-2" /> Zone (Optional)
                      </Form.Label>
                      <Form.Select
                        name="zone_id"
                        value={formData.zone_id}
                        onChange={handleChange}
                      >
                        <option value="">Select Zone</option>
                        {zones.map((z) => (
                          <option key={z.id} value={z.id}>
                            {z.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Billing same as shipping */}
              <Form.Check
                type="checkbox"
                label="Billing address is same as shipping address"
                name="billing_same_as_shipping"
                className="mb-3"
                checked={formData.billing_same_as_shipping}
                onChange={handleChange}
              />

              {/* Billing Section */}
              {!formData.billing_same_as_shipping && (
                <>
                  <h5 className="text-info mt-4 mb-3">
                    <FaExchangeAlt className="me-2" /> Billing Address
                  </h5>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaBuilding className="me-2" /> Address
                    </Form.Label>
                    <Form.Control
                      name="billing_address"
                      value={formData.billing_address}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaPhoneAlt className="me-2" /> Phone Number
                    </Form.Label>
                    <Form.Control
                      name="billing_phone_number"
                      value={formData.billing_phone_number}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaMapPin className="me-2" /> Pincode
                    </Form.Label>
                    <Form.Control
                      name="billing_pincode"
                      value={formData.billing_pincode}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          <FaGlobeAsia className="me-2" /> State
                        </Form.Label>
                        <Form.Select
                          name="billing_state_id"
                          value={formData.billing_state_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select State</option>
                          {states.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          <FaMapMarkerAlt className="me-2" /> District
                        </Form.Label>
                        <Form.Select
                          name="billing_district_id"
                          value={formData.billing_district_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select District</option>
                          {districts.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          <FaCity className="me-2" /> City
                        </Form.Label>
                        <Form.Select
                          name="billing_city_id"
                          value={formData.billing_city_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select City</option>
                          {cities.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {/* Only show billing zone field if zones exist */}
                    {zones.length > 0 && (
                      <Col>
                        <Form.Group>
                          <Form.Label>
                            <FaMapPin className="me-2" /> Zone (Optional)
                          </Form.Label>
                          <Form.Select
                            name="billing_zone_id"
                            value={formData.billing_zone_id}
                            onChange={handleChange}
                          >
                            <option value="">Select Zone</option>
                            {zones.map((z) => (
                              <option key={z.id} value={z.id}>
                                {z.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              <div className="d-grid gap-2 mt-4">
                <Button variant="primary" type="submit" size="lg">
                  <FaCheckCircle className="me-2" /> Submit Address
                </Button>
                <Button
                  variant="outline-secondary"
                  size="lg"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default AddUserAddressDetails;